import type { SxProps, Theme } from "@mui/material/styles";
import { generalSectionSx } from "../Layout";
import {
  MAX_WIDTH,
  DEFAULT_TRANSITION,
  PRIMARY_COLOR,
  BACKGROUND_DARK,
  NEUTRAL_LIGHT,
  NEUTRAL_DARK,
} from "../../theme/consts";

// Scroll container - determines scroll length for sticky effect
// Uses svh (small viewport height) for stable sizing on mobile
export const scrollContainerSx: SxProps<Theme> = {
  height: "150svh",
  position: "relative",
  background: BACKGROUND_DARK,
};

// Sticky container - stays in viewport during scroll
// Uses svh for stable sizing that doesn't change with mobile address bar
export const stickyContainerSx: SxProps<Theme> = {
  position: "sticky",
  top: 0,
  height: "100svh",
  width: "100%",
  maxWidth: MAX_WIDTH,
  display: "flex",
  alignItems: "center",
  // Use clip instead of overflow:hidden to preserve backdrop-filter
  overflow: "clip",
  margin: "0 auto",
};

// Section container - content wrapper
export const sectionContainerSx: SxProps<Theme> = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: { xs: "3rem", md: "0" },
  backgroundColor: BACKGROUND_DARK,
  boxSizing: "border-box",
  ...generalSectionSx,
};

export const titleSx: SxProps<Theme> = {
  fontSize: { xs: "62px", sm: "78px", md: "150px", "2xl": "200px" },
  fontWeight: 700,
  color: "white",
  position: "relative",
  zIndex: 1,
  lineHeight: 1,
};

// Embla viewport - now used for both mobile and desktop
export const emblaViewportSx: SxProps<Theme> = {
  overflow: "hidden",
  width: "100%",
  position: "relative",
  display: "flex",
  // Preserve backdrop-filter within overflow:hidden
  transform: "translateZ(0)",
  willChange: "transform",
  cursor: { xs: "default", md: "grab" },
  "&:active": {
    cursor: { xs: "default", md: "grabbing" },
  },
  // Prevent text selection during carousel drag
  userSelect: "none",
};

// Embla container - flex container for cards
export const emblaContainerSx: SxProps<Theme> = {
  display: "flex",
  gap: { xs: "1rem", md: "2rem" },
  width: "100%",
  // All cards stretch to match the tallest card
  alignItems: "stretch",
  // Ensure children can have backdrop-filter
  transform: "translate3d(0, 0, 0)",
  // Add padding for desktop to allow first/last card positioning
  paddingLeft: 0,
  paddingRight: 0,
};

// Wrapper for carousel and progress bar - animated together on scroll
export const cardsWithProgressWrapperSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  // Don't stretch - fit to content so progress bar/indicators stay close to cards
  flex: "0 0 auto",
  // Gap between cards and progress bar/indicators
  gap: "2rem",
  // Needed for GSAP animation
  willChange: "transform",
  // Preserve backdrop-filter context for cards to blur title underneath
  transform: "translateZ(0)",
  position: "relative",
  zIndex: 2,
};

// Progress bar container - Desktop only (hidden on mobile/tablet)
// Now positioned within wrapper, not absolute
export const progressBarContainerSx: SxProps<Theme> = {
  display: { xs: "none", md: "block" },
  width: "100%",
  flexShrink: 0,
  zIndex: 3,
  userSelect: "none",
  // Extra padding for easier click target
  padding: "0.5rem 0",
};

// Track - click disabled, only thumb is interactive
export const progressBarTrackSx: SxProps<Theme> = {
  width: "100%",
  height: "24px",
  backgroundColor: NEUTRAL_DARK,
  borderRadius: ".75rem",
  position: "relative",
  overflow: "visible", // Allow thumb shadow to show
  border: "1px solid grey",
  cursor: "default", // Click disabled
  transition: "border-color 0.2s ease",
};

// Thumb (fill) - only this is draggable
interface ProgressBarFillParams {
  snapCount: number;
  selectedIndex: number;
  isThumbDragging: boolean;
}

export const getProgressBarFillSx = ({
  snapCount,
  selectedIndex,
  isThumbDragging,
}: ProgressBarFillParams): SxProps<Theme> => ({
  position: "absolute",
  top: 0,
  height: "100%",
  backgroundColor: "white",
  borderRadius: ".75rem",
  boxShadow: "0 0 8px rgba(255, 255, 255, 0.3)",
  width: `${100 / snapCount}%`,
  left: `${selectedIndex * (100 / snapCount)}%`,
  transition: isThumbDragging ? "none" : "left 0.2s ease-out",
  cursor: isThumbDragging ? "grabbing" : "grab",
  touchAction: "none",
  "&:hover": {
    boxShadow: "0 0 12px rgba(255, 255, 255, 0.5)",
  },
});

// Indicators container - only visible on mobile/tablet
export const indicatorsContainerSx: SxProps<Theme> = {
  display: { xs: "flex", md: "none" },
  justifyContent: "center",
  alignItems: "center",
  width: "100%",
  gap: "0.75rem",
};

// Indicator dot styles
export const getIndicatorSx = (isActive: boolean): SxProps<Theme> => ({
  width: "1.25rem",
  height: "1.25rem",
  borderRadius: "50%",
  backgroundColor: isActive ? PRIMARY_COLOR : NEUTRAL_LIGHT,
  transition: DEFAULT_TRANSITION,
  cursor: "pointer",
});
